import React, { useCallback, useEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faVolumeHigh, faVolumeXmark } from "@fortawesome/free-solid-svg-icons";
import { audioBusy, soundCheck } from "../audio/audioEngine";
import { onScreenChange } from "../utils/screenAwake";
import { useSecretCode } from "../hooks/useSecretCode";
import { takeReloadReason } from "../utils/pageReload";
import {
  soundCheckAfterReloadMs,
  soundCheckBadgeMs,
  soundCheckDelayMs,
  soundCheckOnWake,
  wakeAfterDarkMs,
  wakeReload,
} from "../constants";

// Wat de melding zegt per uitkomst van soundCheck() in audio/audioEngine.js.
const messages = {
  played: ["Geluidstest: hoor je de ping?", "Niets? Typ geluid of herstart de Pi"],
  off: ["Geluid staat uit", "Aanzetten in het lab of met ?sound=on"],
  muted: ["Volume staat op 0", "Shift + pijltje omhoog"],
  blocked: ["Browser houdt geluid tegen", "Kioskvlag voor autoplay ontbreekt"],
  none: ["Geen geluid in deze browser", "WebAudio ontbreekt"],
};

// Zolang er een verkoop klinkt wachten we, maar niet eindeloos.
const quietRetryMs = 5000;
const quietTries = 24;

// Na een nacht zonder tv: uitgang vers openen en een ping laten horen, met een melding
// erbij. Laadt de pagina zich dan opnieuw (hooks/useWakeReload.js), dan komt de ping na
// het herladen. De browser kan niet horen of er echt geluid uit de tv komt; wie de ping mist
// weet zo dat het misgaat. Met code doet "geluid" typen dezelfde test meteen; in het lab
// niet, daar zijn g, l, u en d al animaties.
const SoundCheck = ({ code = false }) => {
  const [result, setResult] = useState(null);
  const hideTimer = useRef(null);

  const run = useCallback(async (options) => {
    const outcome = await soundCheck(options);
    console.info(`[dashboard] geluidstest: ${outcome}`);
    setResult(outcome);
    clearTimeout(hideTimer.current);
    hideTimer.current = setTimeout(() => setResult(null), soundCheckBadgeMs);
  }, []);

  useSecretCode(code ? { geluid: () => run({ force: true }) } : {});

  useEffect(() => {
    if (!soundCheckOnWake) return undefined;
    let timer = null;

    const whenQuiet = (left) => {
      timer = null;
      if (audioBusy() && left > 0) {
        timer = setTimeout(() => whenQuiet(left - 1), quietRetryMs);
        return;
      }
      run({ force: false });
    };

    if (takeReloadReason() === "wake") {
      timer = setTimeout(() => whenQuiet(quietTries), soundCheckAfterReloadMs);
    }

    const off = onScreenChange(({ awake, sleptMs }) => {
      clearTimeout(timer);
      timer = null;
      if (!awake || sleptMs < wakeAfterDarkMs || wakeReload) return;
      timer = setTimeout(() => whenQuiet(quietTries), soundCheckDelayMs);
    });

    return () => {
      off();
      clearTimeout(timer);
    };
  }, [run]);

  useEffect(() => () => clearTimeout(hideTimer.current), []);

  if (!result) return null;

  const [title, hint] = messages[result] || messages.none;
  return (
    <span className="sound-check" aria-live="polite">
      <FontAwesomeIcon
        icon={result === "played" ? faVolumeHigh : faVolumeXmark}
        className="sc-icon"
      />
      <span className="sc-text">
        <span className="sc-title">{title}</span>
        <span className="sc-hint">{hint}</span>
      </span>
    </span>
  );
};

export default SoundCheck;
